import { useState, useEffect, useCallback } from 'react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import type { Notification } from '../types';
import { cachedFetch } from '../utils/fetchWithCache';
import type { ActionBreakdown, ActionRow } from '../utils/consultantActionItems';

interface UseActionItemsUnifiedProps {
  accessToken: string;
  currentUser: any;
  companyId?: string; // Optional: if provided, fetches for specific company
}

interface ActionItemsUnifiedResult {
  items: Notification[];
  totalCount: number;
  unreadCount: number;
  requiresUserActionCount: number;
  source: string;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  /** Assigned with a due date and waiting on someone else (consultant global view only). */
  tracking: ActionRow[];
  breakdownCounts?: Partial<ActionBreakdown>;
  debugInfo: {
    source: string;
    count: number;
    rawCount?: number;
    itemsLoaded: number;
  };
}

export function useActionItemsUnified({ accessToken, currentUser, companyId }: UseActionItemsUnifiedProps): ActionItemsUnifiedResult {
  const [items, setItems] = useState<Notification[]>([]);
  const [breakdownCounts, setBreakdownCounts] = useState<Partial<ActionBreakdown> | undefined>(undefined);
  const [tracking, setTracking] = useState<ActionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [debugInfo, setDebugInfo] = useState<{source: string, count: number, rawCount?: number, itemsLoaded: number}>({ 
    source: '', 
    count: 0, 
    itemsLoaded: 0 
  });

  const isConsultant = currentUser?.role === 'consultant' || currentUser?.role === 'admin' || currentUser?.role === 'cfAdmin' || currentUser?.role === 'cf-admin';
  const isSystemAdmin = currentUser?.role === 'systemAdmin' || currentUser?.isAdmin;
  
  // Determine source URL based on role and context
  const getUrl = () => {
    // 1. Consultant Global View (No companyId)
    if (!companyId && isConsultant) {
      return `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/action-items`;
    }
    
    // 2. Client View or Consultant viewing specific Client
    if (companyId) {
      return `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/notifications/${companyId}`;
    }

    // 3. Fallback for others (e.g. system admin global)
    return `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications?userId=${currentUser.id}`;
  };

  const loadItems = useCallback(async () => {
    if (!accessToken) return;
    
    const url = getUrl();
    const sourceName = url.split('/make-server-e90c59cd/')[1] || url;

    try {
      setLoading(true);
      
      const response = await cachedFetch(url, {
        headers: getAuthHeaders(accessToken),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch action items: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.breakdownCounts) {
        setBreakdownCounts(data.breakdownCounts);
      }
      setTracking(Array.isArray(data.tracking) ? data.tracking : []);

      let rawItems: Notification[] = [];
      if (Array.isArray(data)) {
        rawItems = data;
      } else if (Array.isArray(data.actionItems)) {
        rawItems = data.actionItems;
      } else if (Array.isArray(data.notifications)) {
        rawItems = data.notifications;
      } else if (Array.isArray(data.items)) {
        rawItems = data.items;
      }

      const rawCount = rawItems.length;

      // Filter out read items - "Action Items" are inherently unread/pending
      let filteredItems = rawItems.filter((n: Notification) => !(n as any).read);

      // Apply Role-Based Filtering if not using the Consultant Global Endpoint
      // (The Consultant Global Endpoint is trusted to return pre-filtered actionable items)
      // The other endpoints (notifications/:id) return all notifications including messages, which might need filtering?
      // Actually, let's keep the logic consistent:
      
      if (companyId || !isConsultant) {
          filteredItems = filteredItems.filter((n: Notification) => {
            // System Admins and Consultants see ready-for-build notifications
            if ((n.type === 'ready_for_implementation' || n.type === 'ready_for_build') && (isSystemAdmin || isConsultant)) {
              return true;
            }
            // Users see ready-for-review and status change notifications
            if ((n.type === 'ready_for_review' || n.type === 'status_change') && !isSystemAdmin && !isConsultant) {
              return true;
            }
            // Everyone sees messages and homework assigned to them
            if ((n.type === 'message' || n.type === 'homework_assigned') && 
                (n.recipientUserId === currentUser.id || !n.recipientUserId)) {
              return true;
            }
            return false;
          });
      }

      setItems(filteredItems);
      
      const debugData = {
        source: sourceName,
        count: filteredItems.length,
        rawCount,
        itemsLoaded: filteredItems.length
      };
      
      setDebugInfo(debugData);
      
      if (window.location.search.includes('debug=1')) {
         console.log('[ActionItemsUnified] Loaded:', debugData);
      }
      
      setError(null);

    } catch (err) {
      console.error("Action Items Unified Load Failure", err);
      setError(err instanceof Error ? err : new Error('Unknown error loading action items'));
      setDebugInfo(prev => ({ ...prev, source: sourceName }));
    } finally {
      setLoading(false);
    }
  }, [accessToken, companyId, isConsultant, isSystemAdmin, currentUser.id]);

  useEffect(() => {
    // Defer the initial notification fetch so critical dashboard/session
    // calls get connection priority during page load.
    const timer = setTimeout(loadItems, 1500);
    return () => clearTimeout(timer);
  }, [loadItems]);

  // Fail-loud check
  const totalCount = items.length;
  if (totalCount > 0 && items.length === 0) {
      console.error('Data mismatch detected in useActionItemsUnified', { totalCount, itemsLength: items.length });
  }

  // Derived Counts
  // Since we already filtered for unread in loadItems, unreadCount is same as totalCount
  // But strictly speaking, unreadCount usually refers to 'read: false'.
  const unreadCount = totalCount; 
  
  const requiresUserActionCount = items.filter(n => {
     if (isConsultant) {
        return n.type === 'homework_assigned' || n.type === 'ready_for_build' || n.type === 'ready_for_implementation';
     } else {
        return n.type === 'homework_assigned' || n.type === 'ready_for_review';
     }
  }).length;

  return {
    items,
    totalCount,
    unreadCount,
    requiresUserActionCount,
    source: debugInfo.source,
    isLoading: loading,
    error,
    refresh: loadItems,
    tracking,
    breakdownCounts,
    debugInfo
  };
}